import type { ComponentType, SVGProps } from 'react';
import {
  BeakerIcon,
  BookOpenIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import type { ChatComposerMode } from './ChatComposer';
import { cn } from '../../lib/utils';

type IconComponent = ComponentType<SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;

/**
 * Six starter cards shown under the composer on the Orbi empty state.
 * Clicking a card prefills the composer with the prompt and switches
 * to the matching mode — the highest-leverage discoverability fix on
 * the app (see sheet A-000, finding K).
 */

export interface StarterPrompt {
  mode: ChatComposerMode;
  label: string;
  prompt: string;
}

const modeMeta: Record<ChatComposerMode, { icon: IconComponent; label: string; color: string }> = {
  workflow: { icon: BeakerIcon, label: 'Workflow', color: 'text-primary' },
  research: { icon: BookOpenIcon, label: 'Research', color: 'text-blue-600 dark:text-blue-400' },
  portfolio: { icon: FolderIcon, label: 'Portfolio', color: 'text-emerald-600 dark:text-emerald-400' },
};

/**
 * Curated defaults. Kept short so they read as launchpads rather than
 * full briefs — the user is meant to click, edit a word or two, then send.
 * Each carries a program that FLUX and llama will both anchor to.
 */
export const DEFAULT_STARTER_PROMPTS: StarterPrompt[] = [
  {
    mode: 'workflow',
    label: 'Coastal home',
    prompt: 'A three-bedroom coastal home on a sloped site, cedar cladding, cross-ventilated',
  },
  {
    mode: 'workflow',
    label: 'Adaptive reuse',
    prompt: 'A brick warehouse converted to co-working with a top-lit atrium',
  },
  {
    mode: 'research',
    label: 'RIBA stages',
    prompt: 'How do RIBA Plan of Work Stage 2 deliverables differ from Stage 3?',
  },
  {
    mode: 'research',
    label: 'Passive strategies',
    prompt: 'What are the most effective passive cooling strategies for a hot-humid coastal climate?',
  },
  {
    mode: 'portfolio',
    label: 'Case-study intro',
    prompt: 'Draft a two-paragraph case-study introduction for a recently completed housing project',
  },
  {
    mode: 'portfolio',
    label: 'Practice bio',
    prompt: 'Write a sharp 60-word practice biography for a young studio working on cultural and residential buildings',
  },
];

interface OrbiStarterCardsProps {
  onPick: (prompt: StarterPrompt) => void;
  prompts?: StarterPrompt[];
  className?: string;
}

export default function OrbiStarterCards({
  onPick,
  prompts = DEFAULT_STARTER_PROMPTS,
  className,
}: OrbiStarterCardsProps) {
  return (
    <section
      className={cn('w-full', className)}
      aria-label="Suggested prompts to start with"
    >
      <p className="mb-3 text-center text-xs font-medium uppercase tracking-wide text-muted-foreground">
        Or start with
      </p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {prompts.map((p, i) => {
          const meta = modeMeta[p.mode];
          const ItemIcon = meta.icon;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onPick(p)}
              className={cn(
                'group flex flex-col items-start gap-2 rounded-2xl border border-border bg-card px-4 py-3.5 text-left transition-colors',
                'hover:border-primary/60 hover:bg-muted/60',
              )}
            >
              <div className={cn('flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide', meta.color)}>
                <ItemIcon className="h-3.5 w-3.5" />
                <span>{meta.label}</span>
                <span className="text-muted-foreground/70">·</span>
                <span className="text-foreground">{p.label}</span>
              </div>
              <p className="line-clamp-2 text-sm leading-snug text-foreground/85 group-hover:text-foreground">
                {p.prompt}
              </p>
            </button>
          );
        })}
      </div>
    </section>
  );
}
